import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../../../../config/axiosConfig';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';
import SearchableAutocomplete from '../../../../SearchableAutocomplete';

function BankReconciliationForm({ reconciliation, onSuccess, onCancel }) {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        operation: null,
        bank_statement_date: '',
        bank_reference: '',
        notes: '',
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (reconciliation) {
            // Edit mode - populate form with existing data
            setFormData({
                operation: reconciliation.operation || null,
                bank_statement_date: reconciliation.bank_statement_date || '',
                bank_reference: reconciliation.bank_reference || '',
                notes: reconciliation.notes || '',
            });
        } else {
            // Create mode - reset form
            setFormData({
                operation: null,
                bank_statement_date: '',
                bank_reference: '',
                notes: '',
            });
        }
    }, [reconciliation]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        
        if (!formData.operation) {
            newErrors.operation = 'لطفاً عملیات را انتخاب کنید';
        }
        
        if (!formData.bank_statement_date) {
            newErrors.bank_statement_date = 'لطفاً تاریخ صورتحساب بانکی را انتخاب کنید';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const dataToSend = {
                operation: formData.operation?.id || formData.operation,
                bank_statement_date: formData.bank_statement_date,
                bank_reference: formData.bank_reference || '',
                notes: formData.notes || '',
            };
            
            if (reconciliation) {
                // Update existing reconciliation
                await api.patch(`${API.bankReconciliations}${reconciliation.id}/`, dataToSend);
            } else {
                // Create new reconciliation
                await api.post(API.bankReconciliations, dataToSend);
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving bank reconciliation:', error);
            if (error.response?.data) {
                // Set field-specific errors if available
                const fieldErrors = {};
                Object.keys(error.response.data).forEach(key => {
                    if (Array.isArray(error.response.data[key])) {
                        fieldErrors[key] = error.response.data[key][0];
                    } else {
                        fieldErrors[key] = error.response.data[key];
                    }
                });
                setErrors(fieldErrors);
            } else {
                setErrors({ submit: 'خطا در ذخیره تطبیق بانکی. لطفاً دوباره تلاش کنید.' });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '';
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleDateString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SearchableAutocomplete
                        apiUrl={API.operationBrowser}
                        getOptionLabel={(option) => {
                            if (!option) return '';
                            const student = option.student?.full_name || option.student?.first_name + ' ' + option.student?.last_name || '';
                            const course = option.course?.course_name || '';
                            const amount = (option.teacher_wage || 0) + (option.institute_portion || 0);
                            const date = option.date_belonged ? formatDate(option.date_belonged) : '';
                            return `${student} - ${course} - ${formatCurrency(amount)} تومان - ${date}`;
                        }}
                        getOptionValue={(option) => option?.id}
                        value={formData.operation?.id || null}
                        onChange={(value, fullOption) => {
                            handleChange('operation', fullOption);
                        }}
                        label="عملیات"
                        placeholder="جستجوی عملیات..."
                        required
                        error={!!errors.operation}
                        initialOption={reconciliation?.operation_details ? {
                            id: reconciliation.operation,
                            student: { full_name: reconciliation.operation_details?.student_name || '' },
                            course: { course_name: reconciliation.operation_details?.course_name || '' },
                            teacher_wage: 0,
                            institute_portion: reconciliation.operation_details?.amount || 0,
                            date_belonged: reconciliation.operation_details?.date_belonged || '',
                            payment_method_display: reconciliation.operation_details?.payment_method || '',
                            payment_number: reconciliation.operation_details?.payment_number || '',
                        } : null}
                    />
                    {errors.operation && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {errors.operation}
                        </Typography>
                    )}
                    {formData.operation && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>مبلغ:</strong> {formatCurrency((formData.operation.teacher_wage || 0) + (formData.operation.institute_portion || 0))} تومان
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>روش پرداخت:</strong> {formData.operation.payment_method_display || '-'}
                            </Typography>
                            {formData.operation.payment_number && (
                                <Typography variant="body2" color="text.secondary">
                                    <strong>شماره پرداخت:</strong> {formData.operation.payment_number}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Grid>

                <Grid item xs={12} sm={6}>
                    <PersianDatePicker
                        label="تاریخ صورتحساب بانکی"
                        value={formData.bank_statement_date ? new Date(formData.bank_statement_date) : null}
                        onChange={(date) => {
                            if (date && !isNaN(date.getTime())) {
                                handleChange('bank_statement_date', date.toISOString().split('T')[0]);
                            } else {
                                handleChange('bank_statement_date', '');
                            }
                        }}
                        error={!!errors.bank_statement_date}
                        helperText={errors.bank_statement_date}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="شماره مرجع بانکی"
                        value={formData.bank_reference}
                        onChange={(e) => handleChange('bank_reference', e.target.value)}
                        error={!!errors.bank_reference}
                        helperText={errors.bank_reference}
                        placeholder="شماره مرجع از صورتحساب بانکی..."
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="یادداشت"
                        multiline
                        rows={4}
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        error={!!errors.notes}
                        helperText={errors.notes}
                        placeholder="توضیحات مربوط به تطبیق بانکی..."
                    />
                </Grid>

                {errors.submit && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error">
                            {errors.submit}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            onClick={onCancel}
                            disabled={isSubmitting}
                            variant="outlined"
                        >
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'در حال ذخیره...' : (reconciliation ? 'ویرایش' : 'ثبت')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default BankReconciliationForm;

